import type { SearchVehicle, InventoryTableRow, VehicleConfig } from './dto.inventory';

/**
 * Resolves the trim label for a vehicle.
 *
 * Preference order:
 * 1. Bundle name with the model name stripped out (e.g. "Air Grand Touring" → "Grand Touring").
 * 2. Variant name as a fallback when no bundle is present.
 *
 * Localized names take precedence over their default counterparts.
 *
 * @param vehicleConfig - Vehicle configuration from the search API.
 * @returns The formatted trim label, or an empty string when none can be resolved.
 */
const getTrimLabel = (vehicleConfig: VehicleConfig): string => {
  const bundleName = vehicleConfig.bundle?.localizedName || vehicleConfig.bundle?.name;
  if (bundleName) {
    const modelName = vehicleConfig.model?.localizedName || vehicleConfig.model?.name || '';
    return bundleName.replace(modelName, '').trim();
  }
  return vehicleConfig.variant?.localizedName || vehicleConfig.variant?.name || '';
};

/**
 * Transform SearchVehicle from Auctus API to InventoryTableRow for the table
 * @param vehicle - SearchVehicle from the vehicle search API
 * @returns Formatted table row
 */
export const transformSearchVehicleToRow = (vehicle: SearchVehicle): InventoryTableRow => {
  const { vehicleConfig, metadata } = vehicle;

  return {
    vin: metadata.vin,
    webLink: metadata.acWebUrl || '',
    batchNumber: metadata.batchNumber,
    availability: metadata.availability || '',
    locationName: metadata.locationName || '',
    city: metadata.city || '',
    country: metadata.country || '',
    model: vehicleConfig.model?.localizedName || vehicleConfig.model?.name || '',
    trim: getTrimLabel(vehicleConfig),
    drivetrain: vehicleConfig.driveTrain?.localizedName || vehicleConfig.driveTrain?.name || '',
    exteriorColor:
      vehicleConfig.exteriorColor?.localizedName || vehicleConfig.exteriorColor?.name || '',
    exteriorColorCode: vehicleConfig.exteriorColor?.code || '',
    interiorColor:
      vehicleConfig.interiorColor?.localizedName || vehicleConfig.interiorColor?.name || '',
    interiorColorCode: vehicleConfig.interiorColor?.code || '',
    appearance: vehicleConfig.appearance?.localizedName || vehicleConfig.appearance?.name || '',
    appearanceCode: vehicleConfig.appearance?.code || '',
    roof: vehicleConfig.roof?.localizedName || vehicleConfig.roof?.name || '',
    wheels: vehicleConfig.wheels?.localizedName || vehicleConfig.wheels?.name || '',
    wheelsCode: vehicleConfig.wheels?.code || '',
    year: String(metadata.modelYear),
    adas: vehicleConfig.dreamDrive?.localizedName || vehicleConfig.dreamDrive?.name || '',
    adasCode: vehicleConfig.dreamDrive?.code || '',
    soundSystem: vehicleConfig.soundSystem?.localizedName || vehicleConfig.soundSystem?.name || '',
    soundSystemCode: vehicleConfig.soundSystem?.code || '',
    frontSeats: vehicleConfig.frontSeats?.localizedName || vehicleConfig.frontSeats?.name || '',
    frontSeatsCode: vehicleConfig.frontSeats?.code || '',
    rearSeats: vehicleConfig.rearSeats?.localizedName || vehicleConfig.rearSeats?.name || '',
    comfortPackage:
      vehicleConfig.comfortConvenience?.localizedName ||
      vehicleConfig.comfortConvenience?.name ||
      '',
    dynamicHandlingPackage:
      vehicleConfig.dynamicPackage?.localizedName || vehicleConfig.dynamicPackage?.name || '',
    technologyPackage:
      vehicleConfig.techPackage?.localizedName || vehicleConfig.techPackage?.name || '',
    towingPackage:
      vehicleConfig.towingPackage?.localizedName || vehicleConfig.towingPackage?.name || '',
    range: metadata.range ? String(metadata.range) : '',
    power: metadata.power ? String(metadata.power) : '',
    zeroToHundredKmh: metadata.zeroToHundredKmh
      ? String(metadata.zeroToHundredKmh)
      : metadata.accelaration
        ? String(metadata.accelaration)
        : '',
    transportationConfirmed: metadata.transportationConfirmed || '',
    inTransitToLocation: metadata.inTransitToLocation || metadata.transitCountry || '',
    activeFactoryCampaigns: String(metadata.factoryCampaignCount),
    uuid: metadata.uuid || '',
    shortcode: metadata.shortcode || '',
    condition: 'new', // Default - search API doesn't return condition
    price: metadata.totalPrice,
    imageUrl: metadata.imageUrl,
  };
};

/**
 * Transform array of SearchVehicle to table data
 * @param vehicles - Array of SearchVehicle from API
 * @returns Array of formatted table rows
 */
export const transformSearchVehicles = (vehicles: SearchVehicle[]): InventoryTableRow[] => {
  return vehicles.map(transformSearchVehicleToRow);
};
